import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

const EventReminderConfiguration = ({ reminders, onChange, disabled = false }) => {
    const [reminderList, setReminderList] = useState(reminders || [{ days: 7 }]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setReminderList(reminders && reminders.length > 0 ? reminders : [{ days: 7 }]);
        setErrors({});
    }, [reminders]);

    const validateReminders = (list) => {
        const newErrors = {};
        
        for (let i = 0; i < list.length; i++) {
            const reminder = list[i];
            
            if (!reminder.days || reminder.days < 1) {
                newErrors[i] = 'Days must be at least 1';
                continue;
            }
            
            if (reminder.days > 365) {
                newErrors[i] = 'Days cannot exceed 365';
                continue;
            }
            
            if (i > 0) {
                const prevReminder = list[i - 1];
                if (reminder.days >= prevReminder.days) {
                    newErrors[i] = `Must be less than Reminder ${i} (${prevReminder.days} days)`;
                }
            }
        }
        
        return newErrors;
    };

    const handleReminderChange = (index, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue) || value === '') {
            return;
        }

        const newList = [...reminderList];
        newList[index] = { days: numValue };
        setReminderList(newList);
        
        const validationErrors = validateReminders(newList);
        setErrors(validationErrors);
        
        if (onChange && Object.keys(validationErrors).length === 0) {
            onChange(newList);
        }
    };

    const addReminder = () => {
        if (reminderList.length >= 3) return;
        
        const lastReminder = reminderList[reminderList.length - 1];
        const suggestedDays = lastReminder?.days ? Math.max(1, lastReminder.days - 3) : 3;
        
        const newList = [...reminderList, { days: suggestedDays }];
        setReminderList(newList);
        
        const validationErrors = validateReminders(newList);
        setErrors(validationErrors);
        
        if (onChange && Object.keys(validationErrors).length === 0) {
            onChange(newList);
        }
    };

    const removeReminder = (index) => {
        if (reminderList.length <= 1) return;
        
        const newList = reminderList.filter((_, i) => i !== index);
        setReminderList(newList);
        
        const validationErrors = validateReminders(newList);
        setErrors(validationErrors);
        
        if (onChange && Object.keys(validationErrors).length === 0) {
            onChange(newList);
        }
    };

    return (
        <div className="event-reminder-configuration" style={{ marginTop: '1rem' }}>
            <div style={{
                padding: '1rem',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                backgroundColor: '#fafafa'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="pi pi-bell" style={{ color: '#6366f1', fontSize: '1.1rem' }}></i>
                        <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#495057' }}>
                            Event Reminder Configuration
                        </h4>
                    </div>
                    <Button
                        icon="pi pi-plus"
                        label="Add Reminder"
                        className="p-button-sm p-button-text"
                        onClick={addReminder}
                        disabled={disabled || reminderList.length >= 3}
                        tooltip={reminderList.length >= 3 ? 'Maximum 3 reminders allowed' : 'Add another reminder'}
                        tooltipOptions={{ position: 'top' }}
                        style={{ fontSize: '0.875rem' }}
                    />
                </div>

                <div style={{ 
                    padding: '0.65rem 0.85rem', 
                    backgroundColor: '#eff6ff', 
                    border: '1px solid #dbeafe',
                    borderRadius: '4px', 
                    marginBottom: '1rem',
                    fontSize: '0.85rem',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem'
                }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '0.9rem', marginTop: '0.1rem' }}></i>
                    <span>Configure when notifications should be sent before events. Notifications are scheduled when events are created or updated. If the reminder date is in the past, it will be sent immediately.</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {reminderList.map((reminder, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.85rem',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            backgroundColor: '#ffffff'
                        }}>
                            <div style={{ 
                                minWidth: '85px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#6c757d'
                            }}>
                                Reminder {index + 1}
                            </div>
                            
                            <div style={{ flex: 1, maxWidth: '160px' }}>
                                <InputText
                                    id={`reminder-${index}`}
                                    value={reminder.days}
                                    onChange={(e) => handleReminderChange(index, e.target.value)}
                                    placeholder="Days"
                                    disabled={disabled}
                                    keyfilter="int"
                                    className={errors[index] ? 'p-invalid' : ''}
                                    style={{ width: '100%', textAlign: 'center' }}
                                />
                            </div>

                            <div style={{ 
                                fontSize: '0.875rem',
                                color: '#6c757d',
                                minWidth: '70px'
                            }}>
                                days before
                            </div>

                            {reminderList.length > 1 && (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-text p-button-sm"
                                    onClick={() => removeReminder(index)}
                                    disabled={disabled}
                                    tooltip="Remove reminder"
                                    tooltipOptions={{ position: 'top' }}
                                    style={{ minWidth: 'auto', marginLeft: 'auto' }}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {Object.keys(errors).length > 0 && (
                    <div style={{ 
                        marginTop: '0.75rem',
                        padding: '0.65rem 0.85rem',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem'
                    }}>
                        <i className="pi pi-exclamation-circle" style={{ fontSize: '0.9rem', marginTop: '0.1rem' }}></i>
                        <div>
                            {Object.entries(errors).map(([index, error]) => (
                                <div key={index}>Reminder {parseInt(index) + 1}: {error}</div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventReminderConfiguration;
